import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    NgZone,
    OnChanges,
    OnInit,
    Output,
    QueryList, signal,
    Type,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MapComponent as MaplibreMapComponent } from '@maplibre/ngx-maplibre-gl';
import * as pmtiles from 'pmtiles';
import * as maplibregl from 'maplibre-gl';
import {
    GeoJSONSource,
    GPUInitializationError,
    LngLat,
    LngLatBounds,
    Map,
    SourceSpecification,
    StyleSpecification,
} from 'maplibre-gl';
import { MapMarkerWithPopupComponent } from '../map-marker-with-popup/map-marker-with-popup.component';
import { MapItem, MapItemComponent } from '@ui/shared/util-map';
import { AbstractEnvironmentService } from '@ui/shared/domain-config';
import { generateMapLayers } from '../../vendor/utilities/generate-map-layers';
import { Result, ToResult } from '@team23/ts-result';
import { MapBounds } from '../../vendor/definitions/map-bounds';
import { commonMapPoints } from '../../vendor/definitions/common-map-points';
import {
    asyncScheduler,
    BehaviorSubject,
    combineLatest,
    distinctUntilChanged,
    map,
    Observable,
    throttleTime,
} from 'rxjs';
import { isMobile } from '@ui/shared/ui-common';
import { SelectedItemState } from '@ui/shared/domain-map';
import { ApiDataStatus, CoordinateDTO } from '@ui/shared/util-api';
import { coordinatesToLngLat } from '../../vendor/utilities/coordinates-to-lng-lat';
import { MapEventPropagationService } from '../../services/map-event-propagation.service';
import { isFullscreenAvailable } from '../../vendor/utilities/is-fullscreen-available';
import { blankMapSource, clusterMapSource, mapItemAreasMapSource } from '../../vendor/definitions/map-sources';
import { MapZoomService } from '../../_ui/services/map-zoom.service';

const half = 0.5;

/*
 * This controls how often pin clusters are refreshed when the map rerenders, but also leads to a delay for pins to appear or disappear.
 * 200 is a balanced value with a relatively unnoticeable delay and less than 10 refresh actions per render.
 */
const reclusteringGracePeriod = 100;

// A blank layer that will be used to display defined map areas.
const areaMapSource: { areas: SourceSpecification } = {
    areas: {
        type: 'geojson',
        data: {
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'Polygon',
                coordinates: [],
            },
        },
    },
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ui-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    standalone: false,
})
export class MapComponent<MapItemType extends MapItem> implements OnInit, OnChanges {
    @Input({ required: true })
    public popupContentComponentType!: Type<MapItemComponent<MapItemType>>;

    @Input({ required: true })
    public sidebarIsMinimized = false;

    @Input()
    public isShowingMap = false;

    @Input()
    public isFullscreen = false;

    @Output()
    public readonly markerClicked = new EventEmitter<MapItemType>();

    @Output()
    public readonly sidebarMinimized = new EventEmitter();

    @Output()
    public readonly onlyShowMapItems = new EventEmitter();

    @ViewChild(MaplibreMapComponent, { static: true })
    public mapComponent?: MaplibreMapComponent;

    @ViewChild('map', { static: true, read: ElementRef })
    public mapElement?: ElementRef<HTMLElement>;

    @ViewChildren(MapMarkerWithPopupComponent)
    public mapMarkerWithPopupComponents?: QueryList<MapMarkerWithPopupComponent<MapItemType>>;

    public mapStyles: StyleSpecification;

    public mapBounds: LngLatBounds;

    public mapPoints = commonMapPoints;

    public isFullscreenAvailable = false;

    public isWebglUnavailable = signal(false);

    public readonly visibleMapMarkerIds$: Observable<Record<string, boolean>>;

    private _isMobile = false;
    private _mapAreas: Result<Array<Array<CoordinateDTO>>, ApiDataStatus> = ToResult.fromData([]);
    private _mapItems: Result<Array<MapItemType>, ApiDataStatus> = ToResult.fromData([]);
    private readonly _shouldUpdateClusters$ = new BehaviorSubject<true>(true);
    private readonly _shouldUpdateClusterSettings$ = new BehaviorSubject<true>(true);

    protected readonly _zoomService = inject(MapZoomService);

    public mapZoom = toSignal(this._zoomService.zoom$, { requireSync: true });

    constructor(
        private readonly _selectedMapItemService: SelectedItemState<MapItemType>,
        private readonly _mapEventPropagationService: MapEventPropagationService,
        private readonly _ngZone: NgZone,
        private readonly _environmentService: AbstractEnvironmentService,
    ) {
        const pmtilesPath = this._environmentService.environment.data?.pmtilesPath;

        this.mapStyles = {
            layers: generateMapLayers('protomaps'),
            version: 8,
            glyphs: 'assets/feature-map/fonts/{fontstack}/{range}.pbf',
            sources: {
                // Load pmtiles file unless tiles are skipped in the environment.
                protomaps: pmtilesPath
                    ? {
                        type: 'vector',
                        url: `pmtiles://${ pmtilesPath }`,
                    }
                    : blankMapSource.protomaps,

                ...clusterMapSource,
                // Configure the cluster source with cluster settings matching the screen width.
                pins: {
                    ...clusterMapSource.pins,
                    ...this._determineClusterSettings(),
                },
                ...mapItemAreasMapSource,
                ...areaMapSource,
            },
        };

        this.mapBounds = new LngLatBounds(
            new LngLat(MapBounds.west, MapBounds.south),
            new LngLat(MapBounds.east, MapBounds.north),
        );

        // Update the list of map items that are not in a cluster, or are currently selected.
        this.visibleMapMarkerIds$ = combineLatest([

            /*
             * Whenever the map triggers a cluster update, collect all markers that are not in a cluster.
             * This is throttled because the map updates multiple times during any action.
             * The throttle ignores the first triggers per action, but ensures that at least one is sent.
             */
            this._shouldUpdateClusters$
                .pipe(
                    throttleTime(reclusteringGracePeriod, asyncScheduler, { leading: false, trailing: true }),
                    map(() => this._getUnclusteredMapMarkers()),
                    distinctUntilChanged((previous, current) =>
                        previous.sort().toString() === current.sort().toString(),
                    ),
                ),
            this._selectedMapItemService.mapItem$
                .pipe(
                    distinctUntilChanged(),
                ),
        ])
            .pipe(
                takeUntilDestroyed(),
                map(([clusteredMapItemIds, selectedMapItem]) =>
                    clusteredMapItemIds
                        .concat(selectedMapItem?.uniqueId ? [selectedMapItem.uniqueId] : [])
                        .reduce((result, current) => ({ ...result, [current]: true }), {}),
                ),
            );

        this._shouldUpdateClusterSettings$
            .pipe(
                takeUntilDestroyed(),
                throttleTime(reclusteringGracePeriod, asyncScheduler, { leading: true, trailing: true }),
            )
            .subscribe(() => this._updateClusterSettings());
    }

    public get isMobile(): boolean {
        return this._isMobile;
    }

    @Input()
    public set isMobile(value) {

        /*
         * If the screen switches between mobile and desktop, remove or restore padding as appropriate.
         * If a mapitem is active, padding should be set on desktop but not on mobile.
         */
        if (this._isMobile !== value) {
            if (this._selectedMapItemService.getMapItem()) {
                const height = window.innerHeight;

                this.mapComponent?.mapInstance.easeTo({
                    padding: { top: height * (value ? 0 : half) },
                });
            }
        }

        this._isMobile = value;
    }

    public get mapItems(): Result<Array<MapItemType>, ApiDataStatus> {
        return this._mapItems;
    }

    @Input()
    public set mapItems(value: Result<Array<MapItemType>, ApiDataStatus>) {
        this._mapItems = value;

        this._replaceMapItemPins();
        this._replaceMapItemAreas();
    }

    public get mapAreas(): Result<Array<Array<CoordinateDTO>>, ApiDataStatus> {
        return this._mapAreas;
    }

    @Input()
    public set mapAreas(value: Result<Array<Array<CoordinateDTO>>, ApiDataStatus> | undefined | null) {
        this._mapAreas = value ?? ToResult.fromData([]);

        this._replaceMapAreas();
    }

    public get isMapDataLoading(): boolean {
        return [this.mapItems.error?.key, this.mapAreas.error?.key].includes(ApiDataStatus.Loading);
    }

    @HostListener('window:resize')
    public onResize(): void {
        // Check the cluster radius whenever the window resizes.
        this._shouldUpdateClusterSettings$.next(true);
    }

    public ngOnInit(): void {
        const protocol = new pmtiles.Protocol();

        maplibregl.addProtocol('pmtiles', protocol.tile);

        this.isFullscreenAvailable = this._isFullscreenAvailable();
    }

    public resizeMap(): void {
        const instance = this.mapComponent?.mapInstance;

        instance?.resize();
    }

    public handleMarkerPopupOpened(mapItem: MapItemType): void {
        this._moveToMarker(mapItem);
    }

    public handlePopupClosed(mapItem: MapItemType): void {
        if (!this._selectedMapItemService.getMapItem()) {
            if (!mapItem.mapCoordinates) {
                return;
            }

            this.mapComponent?.mapInstance.easeTo({
                center: coordinatesToLngLat(mapItem.mapCoordinates),
                padding: { top: 0 },
            });
        }
    }

    protected _interval?: number = undefined;

    public ngOnChanges(): void {
        clearTimeout(this._interval);

        this._interval = setTimeout(() => this.resizeMap());
    }

    public async maximizeMapWithSidebar(): Promise<void> {
        await document.querySelector('#map-with-sidebar')?.requestFullscreen({ navigationUI: 'hide' });
        this.resizeMap();
    }

    public async minimizeMapWithSidebar(): Promise<void> {
        await document.exitFullscreen();
        this.resizeMap();
    }

    public updateMapZoom(event: { target: Map }): void {
        this._zoomService.setZoom(event.target.getZoom());
    }

    /**
     * Sets up listeners for the map as soon as it's loaded.
     *
     * @param mapInstance The Map instance passed from the component when it has finished loading.
     */
    public setupMap(mapInstance: Map): void {
        this._replaceMapItemPins();
        this._replaceMapItemAreas();

        /*
         * Whenever the map rerenders (resizes, zooms, pans, changes content etc.), update the clusters.
         * The reclustering is debounced because the map rerenders multiple times during one change.
         */
        mapInstance.on('render', () => {
            // NgZone is required because the change detector doesn't work within callbacks.
            this._ngZone.run(() => {
                this._shouldUpdateClusters$.next(true);
            });
        });

        /*
         * When a cluster is clicked, zoom as far in as needed to expand it, then move the map to its center.
         * Close any open popups at the same time and remove map padding.
         */
        mapInstance.on('click', 'clusters_pins', event => {
            const clusterFeature = event.features?.find(feature => feature.properties['cluster']);
            const clusterId = clusterFeature?.properties['cluster_id'] as number | undefined;

            /*
             * Don't allow clicking the cluster if the event is marked to be ignored.
             * This prevents zoom conflicts when you click a marker that is over a cluster.
             */
            if (this._mapEventPropagationService.shouldIgnoreEvent(event.originalEvent)) {
                return;
            }

            this._mapEventPropagationService.registerEvent(event.originalEvent);

            if (!clusterFeature || clusterId === undefined) {
                return;
            }

            (mapInstance.getSource('pins') as GeoJSONSource).getClusterExpansionZoom(
                clusterId,
            ).then(zoom => {
                if (!zoom) {
                    return;
                }

                // NgZone is required because the change detector doesn't work within callbacks.
                this._ngZone.run(() => {
                    this._selectedMapItemService.setMapItem(null);
                });

                mapInstance.easeTo({
                    center: (clusterFeature.geometry as GeoJSON.Point).coordinates as [number, number],
                    zoom,
                    padding: { top: 0 },
                });
            });
        });

        mapInstance.on('mouseenter', 'clusters_pins', () => {
            mapInstance.getCanvas().style.cursor = 'pointer';
        });

        mapInstance.on('mouseleave', 'clusters_pins', () => {
            mapInstance.getCanvas().style.cursor = '';
        });

        mapInstance.on('mouseenter', 'map_item_areas_fill', () => {
            mapInstance.getCanvas().style.cursor = 'pointer';
        });

        mapInstance.on('mouseleave', 'map_item_areas_fill', () => {
            mapInstance.getCanvas().style.cursor = '';
        });

        mapInstance.on('click', 'map_item_areas_fill', event => {

            /*
             * Don't allow clicking the area if a map item is open.
             * This prevents the area from being triggered when you click outside of a popup to close it, but click on the area by doing so.
             */
            if (this._selectedMapItemService.getMapItem()) {
                return;
            }

            /*
             * Don't allow clicking the area if the event is marked to be ignored.
             * This prevents the area from being triggered when you click a cluster or a marker that is over an area.
             */
            if (this._mapEventPropagationService.shouldIgnoreEvent(event.originalEvent)) {
                return;
            }

            const areaFeature = event.features?.find(feature => feature.source === 'mapItemAreas');
            const mapItemId = areaFeature?.properties['mapItemId'] as unknown;

            const mapItemById = this.mapItems.data?.find(mapItem =>
                mapItem.uniqueId === mapItemId,
            );

            if (mapItemById) {
                this._selectedMapItemService.setMapItem(mapItemById);
            }
        });
    }

    /**
     * Handles errors fired by the underlying maplibre-gl map instance.
     *
     * Some visitors' browsers refuse to create a WebGL2 context (e.g. GPU driver blocklisting,
     * disabled hardware acceleration, or running in a VM). That's an environment limitation we
     * cannot fix, so it's reported to Bugsnag as an info-level event instead of a crash, and a
     * fallback message is shown instead of a blank map.
     */
    public handleMapError(event: { error: unknown }): void {
        if (!(event.error instanceof GPUInitializationError)) {
            console.error(event.error);

            return;
        }

        this.isWebglUnavailable.set(true);
    }

    private _updateClusterSettings(): void {
        const mapInstance = this.mapComponent?.mapInstance;

        if (!mapInstance) {
            return;
        }

        const { cluster: shouldCluster, clusterRadius } = this._determineClusterSettings();

        (mapInstance.getSource('pins') as GeoJSONSource | null)
            ?.setClusterOptions({
                cluster: shouldCluster,
                clusterRadius,
            });
    }

    private _determineClusterSettings(): { clusterRadius: number; cluster: boolean } {

        /*
         * unfortunately it is not possible to detect the visual map radius after clicking on a cluster
         * (it always changes the zoom size by 1, see `.getClusterExpansionZoom`)
         * to avoid pins being out of the visual screen, adjust the safe padding
         */
        const safePadding = 80;
        const maxClusterRadius = 200;
        const safeClusterRadius = window.innerWidth * half - safePadding;

        return {
            clusterRadius: Math.min(safeClusterRadius, maxClusterRadius),
            cluster: safeClusterRadius > 0,
        };
    }

    /**
     * Collects the IDs of map points on the 'pins' layer that are not hidden in a cluster.
     */
    private _getUnclusteredMapMarkers(): Array<string> {
        if (!this.mapItems.data || !this.mapComponent?.mapInstance) {
            return [];
        }

        return Array.from(
            new Set(
                this.mapComponent.mapInstance
                    .querySourceFeatures('pins')
                    .map(feature =>
                        feature.properties['mapItemId'] as string | undefined ?? 'noId',
                    ),
            ),
        );
    }

    private _moveToMarker(mapItem: MapItemType): void {
        const height = window.innerHeight;

        if (!mapItem.mapCoordinates) {
            return;
        }

        this.mapComponent?.mapInstance.easeTo({
            center: coordinatesToLngLat(mapItem.mapCoordinates),

            /*
             * Always open markers at zoom level 16, where all clusters are dissolved.
             * That way, no open markers are displayed over a cluster.
             */
            zoom: 16,
            // Move the map view frame down by half the viewport height in order to make space for the popup.
            padding: { top: height * (isMobile() ? 0 : half) },
        });
    }

    /**
     * Fill the clusters data with a map point for each map item.
     * The point is stamped with the map item's ID for later reconnection.
     * This is only done with mapItems that are not areas.
     *
     * This causes the map to rerender and update clustering.
     */
    private _replaceMapItemPins(): void {
        if (!this.mapComponent?.mapInstance) {
            return;
        }

        const mapInstance = this.mapComponent.mapInstance;

        (mapInstance.getSource('pins') as GeoJSONSource | null)?.setData({
            type: 'FeatureCollection',
            features:
                this._mapItems.data
                    ?.filter(mapItem => !mapItem.isArea)
                    .filter(mapItem => !!mapItem.mapCoordinates)
                    .map(mapItem => {
                        if (!mapItem.mapCoordinates) {
                            throw new Error('Missing Coordinates');
                        }

                        return {
                            type: 'Feature',
                            geometry: {
                                type: 'Point',
                                coordinates: [mapItem.mapCoordinates.longitude, mapItem.mapCoordinates.latitude],
                            },
                            properties: {
                                mapItemId: mapItem.uniqueId,
                            },
                        };
                    })
                ?? [],
        });
    }

    /**
     * Fill the map item areas data with polygon coordinates for each map item.
     * The area is stamped with the map item's ID for later reconnection.
     * This is only done with mapItems that are areas.
     *
     * This causes the map to rerender and update clustering.
     */
    private _replaceMapItemAreas(): void {
        if (!this.mapComponent?.mapInstance) {
            return;
        }

        const mapInstance = this.mapComponent.mapInstance;

        (mapInstance.getSource('mapItemAreas') as GeoJSONSource | null)?.setData({
            type: 'FeatureCollection',
            features:
                this._mapItems.data
                    ?.filter(mapItem => mapItem.isArea)
                    .map(mapItem => ({
                        type: 'Feature',
                        geometry: {
                            type: 'Polygon',
                            coordinates: [
                                mapItem.mapArea
                                    ?.map(point => ([point.longitude, point.latitude]))
                                ?? [],
                            ],
                        },
                        properties: {
                            mapItemId: mapItem.uniqueId,
                        },
                    }))
                ?? [],
        });
    }

    /**
     * Fill the areas data with polygon coordinates.
     *
     * This causes the map to rerender and update clustering.
     */
    private _replaceMapAreas(): void {
        if (!this.mapComponent?.mapInstance) {
            return;
        }

        const mapInstance = this.mapComponent.mapInstance;

        (mapInstance.getSource('areas') as GeoJSONSource | null)?.setData({
            type: 'FeatureCollection',
            features: this._mapAreas.data?.map(mapArea => ({
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [
                        mapArea.map(point => ([point.longitude, point.latitude])),
                    ],
                },
                properties: {},
            })) ?? [],
        });
    }

    /**
     * Determine if the fullscreen api can be used in the current browser.
     */
    private _isFullscreenAvailable(): boolean {
        return isFullscreenAvailable();
    }
}
